import { Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { DraftOrder, PostalAddress } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import { countryName } from '@/features/markets';

import { DraftAddressDialog, type AddressKind } from './draft-address-dialog';

function addressLines(address: PostalAddress): string[] {
  const name = [address.firstName, address.lastName].filter(Boolean).join(' ');
  const cityLine = [address.city, address.region, address.postalCode].filter(Boolean).join(', ');
  return [
    name,
    address.company ?? '',
    address.line1,
    address.line2 ?? '',
    cityLine,
    countryName(address.countryCode),
    address.phone ?? '',
  ].filter(Boolean);
}

function AddressBlock({
  label,
  address,
  fallback,
  editLabel,
  onEdit,
}: {
  label: string;
  address: PostalAddress | null;
  fallback: string;
  editLabel: string;
  onEdit: (() => void) | null;
}) {
  const { t } = useTranslation('draftOrders');
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
          {label}
        </h3>
        {onEdit ? (
          <Button variant="ghost" size="sm" onClick={onEdit} aria-label={editLabel}>
            {address ? <Pencil aria-hidden /> : <Plus aria-hidden />}
            {address ? t('actions.edit', { ns: 'common' }) : t('address.add')}
          </Button>
        ) : null}
      </div>
      {address ? (
        <address className="text-sm not-italic">
          {addressLines(address).map((line, i) => (
            <span key={i} className="block">
              {line}
            </span>
          ))}
        </address>
      ) : (
        <p className="text-muted-foreground text-sm">{fallback}</p>
      )}
    </div>
  );
}

export interface DraftAddressesCardProps {
  storeId: string;
  draft: DraftOrder;
  editable: boolean;
}

/** Shipping decides which rates apply; an empty billing address means "same as shipping". */
export function DraftAddressesCard({ storeId, draft, editable }: DraftAddressesCardProps) {
  const { t } = useTranslation('draftOrders');
  const [editing, setEditing] = useState<AddressKind | null>(null);

  return (
    <FormSection title={t('address.title')}>
      <AddressBlock
        label={t('address.shipping')}
        address={draft.shippingAddress}
        fallback={t('address.none', { ns: 'orders' })}
        editLabel={t('address.editShipping')}
        onEdit={editable ? () => setEditing('shippingAddress') : null}
      />
      <AddressBlock
        label={t('address.billing')}
        address={draft.billingAddress}
        fallback={t('address.sameAsShipping')}
        editLabel={t('address.editBilling')}
        onEdit={editable ? () => setEditing('billingAddress') : null}
      />
      {editing ? (
        <DraftAddressDialog
          storeId={storeId}
          draft={draft}
          kind={editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}
    </FormSection>
  );
}
